import { ApiResponse } from 'apisauce';
import { ErrorInfo } from '../../../shared/interfaces/error-info.interface';
import api from '../../../shared/services/utils/api/api.service';
import errorHelper from '../../../shared/services/utils/api/error-helper.service';

export const getModuleName = () => 'operation-module';

export interface ReservationsOperationsQuery {
  unitTypeId?: number;
  cityId?: string;
  areaId?: string;
  status?: string | string[];
  fromDate?: number;
  toDate?: number;
  propertyManagerId?: string;
  sortBy?: 'checkInDate' | 'checkOutDate';
}

export type ReservationsOperationsItem = {
  id: number;
  type: 'reservation' | 'blockDate' | string;
  unitTypeId?: number;
  unitTypeName?: string;
  roomId?: string;
  roomName?: string;
  propertyName?: string;
  areaName?: string;
  cityName?: string;
  checkInDate?: number;
  checkOutDate?: number;
  numberOfNights?: number;
  primaryGuestName?: string;
  primaryGuestPhone?: string;
  status?: string;
  source?: string;
  createdByUsername?: string;
  createdAt?: string;
  updatedAt?: string;
};

export type ReservationsOperationsSearchResponse = {
  data: ReservationsOperationsItem[];
  count?: number;
};

export type MaintenanceRequestStatus = 'new' | 'in_progress' | 'done';

export interface MaintenanceRequestComment {
  comment: string;
  writtenById: string;
  writtenByUsername: string;
}

export type MaintenanceRequestItem = {
  id?: number;
  reservationId: number;
  unitId: number;
  unitShortName: string;
  requestDate: number;
  description: string;
  createdById: string;
  createdByUsername: string;
  status: MaintenanceRequestStatus;
  comments: MaintenanceRequestComment[];
  createdAt?: Date | string;
  updatedAt?: Date | string;
};

export type MaintenanceRequestsSearchResponse = {
  data: MaintenanceRequestItem[];
  count?: number;
};

export type ReservationOperationMaintenancePayload = {
  description: string;
  reservationId: number | string;
};

export type UpdateMaintenanceRequestPayload = {
  status?: MaintenanceRequestStatus;
  comment?: string;
};

export async function reservationsOperationsSearch(
  query: ReservationsOperationsQuery,
  offset: number,
  limit: number
): Promise<ReservationsOperationsSearchResponse | ErrorInfo> {
  const normalized: ApiResponse<any, ErrorInfo> = await (
    api as any
  ).getReservationsOperationsSearch(query, offset, limit);

  if (normalized.ok) {
    const payload: any = normalized.data;

    // Most endpoints return ResponseModel: { data: <list>, count?: number }
    const list = payload?.data?.data ?? payload?.data ?? [];
    const count = payload?.data?.count ?? payload?.count;

    if (Array.isArray(list)) {
      return { data: list, count };
    }

    return errorHelper(0, '', getModuleName(), 'reservationsOperationsSearch');
  }

  return errorHelper(
    Number(normalized.status),
    (normalized.data as any)?.errorCode
      ? String((normalized.data as any)?.errorCode || '')
      : normalized.problem,
    getModuleName(),
    'reservationsOperationsSearch'
  );
}

export async function createReservationOperationMaintenance(
  payload: ReservationOperationMaintenancePayload
): Promise<{} | ErrorInfo> {
  const normalized: ApiResponse<any, ErrorInfo> = await (
    api as any
  ).createReservationOperationMaintenance(payload);

  if (normalized.ok) {
    return {};
  }

  return errorHelper(
    Number(normalized.status),
    (normalized.data as any)?.errorCode
      ? String((normalized.data as any)?.errorCode || '')
      : normalized.problem,
    getModuleName(),
    'createReservationOperationMaintenance'
  );
}

export async function searchMaintenanceRequests(
  query: {},
  offset: number,
  limit: number
): Promise<MaintenanceRequestsSearchResponse | ErrorInfo> {
  const normalized: ApiResponse<any, ErrorInfo> = await (api as any).searchMaintenanceRequests(
    query,
    offset,
    limit
  );

  if (normalized.ok) {
    const payload: any = normalized.data;
    const list = payload?.data?.data ?? payload?.data ?? [];
    const count = payload?.data?.count ?? payload?.count;

    if (Array.isArray(list)) {
      return { data: list, count };
    }

    return errorHelper(0, '', getModuleName(), 'searchMaintenanceRequests');
  }

  return errorHelper(
    Number(normalized.status),
    (normalized.data as any)?.errorCode
      ? String((normalized.data as any)?.errorCode || '')
      : normalized.problem,
    getModuleName(),
    'searchMaintenanceRequests'
  );
}

export async function updateMaintenanceRequest(
  maintenanceRequestId: number | string,
  payload: UpdateMaintenanceRequestPayload
): Promise<{} | ErrorInfo> {
  const normalized: ApiResponse<any, ErrorInfo> = await (api as any).updateMaintenanceRequest(
    maintenanceRequestId,
    payload
  );

  if (normalized.ok) {
    return {};
  }

  return errorHelper(
    Number(normalized.status),
    (normalized.data as any)?.errorCode
      ? String((normalized.data as any)?.errorCode || '')
      : normalized.problem,
    getModuleName(),
    'updateMaintenanceRequest'
  );
}
